import { type GenericAbortSignal } from 'axios';

import { type SDCreateRequest } from '@/components/Shared/widgets/service-desk-create/types/form';
import {
  type TSDAttachment,
  type TSDDetailedResponse,
  type TSDMessageCountResponse,
  type TSDResponse
} from '@/components/Shared/widgets/types/serviceDesk.type';
import {
  type ServiceDeskFilters,
  type TChangeStatusServiceDeskRequest,
  type TSendMessageServiceDeskRequest
} from '@/store/serviceDesk.slice';

import { ApiConnector } from '../api.connector';

class ServiceDeskApi extends ApiConnector {
  getServiceDeskRequests(
    isAdmin: boolean,
    filters: ServiceDeskFilters
  ): Promise<
    [
      {
        total: number;
        data: TSDResponse[];
      },
      TSDMessageCountResponse[]
    ]
  > {
    return this.call({
      path: isAdmin ? 'admin/support' : 'support',
      body: filters,
      method: 'post'
    });
  }

  getServiceDeskRequest(
    isAdmin: boolean,
    requestId: string
  ): Promise<TSDDetailedResponse> {
    return this.call({
      path: `${isAdmin ? 'admin/support' : 'support'}/${requestId}`
    });
  }

  createServiceDeskRequest(data: SDCreateRequest): Promise<string> {
    return this.call({
      path: 'support/create',
      body: data,
      method: 'post'
    });
  }

  sendMessageServiceDeskRequest({
    isAdmin,
    requestId,
    ...rest
  }: TSendMessageServiceDeskRequest): Promise<void> {
    return this.call({
      path: `${isAdmin ? 'admin/support' : 'support'}/${requestId}/send_msg`,
      body: rest,
      method: 'post'
    });
  }

  changeStatusServiceDeskRequest({
    isAdmin,
    requestId,
    status_uuid,
    reason_uuid,
    comment
  }: TChangeStatusServiceDeskRequest): Promise<void> {
    return this.call({
      path: `${isAdmin ? 'admin/support' : 'support'}/${requestId}`,
      body: { status_uuid, reason_uuid, comment },
      method: 'patch'
    });
  }

  setIsReadChatItems({
    isAdmin,
    requestId
  }: {
    isAdmin: boolean;
    requestId: string;
  }): Promise<void> {
    return this.call({
      path: `${isAdmin ? 'admin/support' : 'support'}/${requestId}/is_read`
    });
  }

  startUserBadgeServiceDesk(
    userId: string
  ): Promise<TSDMessageCountResponse[]> {
    return this.call({
      path: `support/newCount/byUser/${userId}`
    });
  }

  startAdminBadgeNewServiceDesk(): Promise<number> {
    return this.call({
      path: 'admin/support/count/newCount'
    });
  }

  startAdminBadgeReworkServiceDesk(): Promise<number> {
    return this.call({
      path: 'admin/support/count/reworkCount'
    });
  }

  downloadServiceDeskFiles(
    uuid: string,
    abortSignal?: GenericAbortSignal
  ): Promise<TSDAttachment[]> {
    return this.call({
      path: `support/${uuid}/download`,
      abortSignal
    });
  }

  downloadServiceDeskChatItemFile(
    uuid: string,
    abortSignal?: GenericAbortSignal
  ): Promise<[TSDAttachment]> {
    return this.call({
      path: `support/${uuid}/download-chat-file`,
      abortSignal
    });
  }
}

export const serviceDeskApi = new ServiceDeskApi();
